import { useTranslation } from '../../../i18n';
import React, { useState } from 'react';
import {
    Card,
    Form,
    Input,
    Switch,
    Button,
    Space,
    List,
    Typography,
    Row,
    Col,
    message
} from 'antd';
import {
    CloudDownloadOutlined,
    DatabaseOutlined,
    UploadOutlined,
    HistoryOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const { TextArea } = Input;

const ShellTools = ({ vhostName }) => {
    const { t } = useTranslation();
    const [wgetLoading, setWgetLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [queryLoading, setQueryLoading] = useState(false);
    const [logs, setLogs] = useState([]);

    const appendLog = (action, resp) => {
        setLogs((prev) => [
            {
                action,
                success: resp.success,
                message: resp.message || resp.error || (resp.success ? t('Operation successful') : t('Operation failed')),
                data: resp.data || {}
            },
            ...prev
        ].slice(0, 10));
    };

    const handleWget = async (values) => {
        setWgetLoading(true);
        try {
            const response = await hostSharedService.shellWget(
                vhostName,
                values.url,
                values.dir,
                values.filename,
                values.continue || false
            );
            if (!response.success) {
                message.error(response.error || t('Failed to start wget'));
            } else {
                message.success(response.message || t('wget task started'));
            }
            appendLog(t('wget download'), response);
        } catch (error) {
            message.error(t('Failed to start wget: {{message}}', { message: error.message }));
        } finally {
            setWgetLoading(false);
        }
    };

    const handleExportDb = async (values) => {
        setExportLoading(true);
        try {
            const response = await hostSharedService.exportDatabase(vhostName, values.password);
            if (!response.success) {
                message.error(response.error || t('Database export failed'));
            } else {
                message.success(response.message || t('Database export started'));
            }
            appendLog(t('Export database'), response);
        } catch (error) {
            message.error(t('Database export failed: {{message}}', { message: error.message }));
        } finally {
            setExportLoading(false);
        }
    };

    const handleImportDb = async (values) => {
        setImportLoading(true);
        try {
            const response = await hostSharedService.importDatabase(vhostName, values.password, values.file);
            if (!response.success) {
                message.error(response.error || t('Database import failed'));
            } else {
                message.success(response.message || t('Database import started'));
            }
            appendLog(t('Import database'), response);
        } catch (error) {
            message.error(t('Database import failed: {{message}}', { message: error.message }));
        } finally {
            setImportLoading(false);
        }
    };

    const handleQueryShell = async (values) => {
        setQueryLoading(true);
        try {
            const response = await hostSharedService.queryShell(vhostName, values.session);
            if (!response.success) {
                message.error(response.error || t('Query failed'));
            } else {
                message.success(t('Query successful'));
            }
            appendLog(t('Task lookup'), response);
        } catch (error) {
            message.error(t('Query failed: {{message}}', { message: error.message }));
        } finally {
            setQueryLoading(false);
        }
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row gutter={16}>
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space>
                                <CloudDownloadOutlined />
                                {t('wget file download')}
                            </Space>
                        }
                    >
                        <Form layout="vertical" onFinish={handleWget}>
                            <Form.Item
                                name="url"
                                label={t('Download link')}
                                rules={[{ required: true, message: t('Please enter a download link') }]}
                            >
                                <Input placeholder="http://example.com/file.zip" />
                            </Form.Item>
                            <Form.Item
                                name="dir"
                                label={t('Save directory')}
                                rules={[{ required: true, message: t('Please enter a save directory') }]}
                                initialValue="/www/wwwroot"
                            >
                                <Input placeholder="/www/wwwroot" />
                            </Form.Item>
                            <Form.Item
                                name="filename"
                                label={t('File name')}
                                rules={[{ required: true, message: t('Please enter a filename') }]}
                            >
                                <Input placeholder="file.zip" />
                            </Form.Item>
                            <Form.Item name="continue" label={t('Resumable transfers')} valuePropName="checked">
                                <Switch />
                            </Form.Item>
                            <Button type="primary" htmlType="submit" loading={wgetLoading} block>
                                {t('Start download')}
                            </Button>
                        </Form>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space>
                                <DatabaseOutlined />
                                {t('Export database')}
                            </Space>
                        }
                    >
                        <Form layout="vertical" onFinish={handleExportDb}>
                            <Form.Item
                                name="password"
                                label={t('Database password')}
                                rules={[{ required: true, message: t('Please enter a database password') }]}
                            >
                                <Input.Password placeholder={t('Database password')} />
                            </Form.Item>
                            <Button type="primary" htmlType="submit" loading={exportLoading} block>
                                {t('Start export')}
                            </Button>
                        </Form>
                    </Card>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space>
                                <UploadOutlined />
                                {t('Import database')}
                            </Space>
                        }
                    >
                        <Form layout="vertical" onFinish={handleImportDb}>
                            <Form.Item
                                name="file"
                                label={t('SQL/archive file path')}
                                rules={[{ required: true, message: t('Please enter a file path') }]}
                            >
                                <Input placeholder="/backup/db.sql" />
                            </Form.Item>
                            <Form.Item
                                name="password"
                                label={t('Database password')}
                                rules={[{ required: true, message: t('Please enter a database password') }]}
                            >
                                <Input.Password placeholder={t('Database password')} />
                            </Form.Item>
                            <Button type="primary" htmlType="submit" loading={importLoading} block>
                                {t('Start import')}
                            </Button>
                        </Form>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <Space>
                                <HistoryOutlined />
                                {t('Task lookup')}
                            </Space>
                        }
                    >
                        <Form layout="vertical" onFinish={handleQueryShell}>
                            <Form.Item
                                name="session"
                                label="Session"
                                rules={[{ required: true, message: t('Please enter the task Session') }]}
                            >
                                <Input placeholder={t('Copy the session id from the task result')} />
                            </Form.Item>
                            <Button type="primary" htmlType="submit" loading={queryLoading} block>
                                {t('Query task')}
                            </Button>
                        </Form>
                    </Card>
                </Col>
            </Row>

            <Card title={t('Task history')}>
                <List
                    dataSource={logs}
                    locale={{ emptyText: t('No operations yet') }}
                    renderItem={(item) => (
                        <List.Item>
                            <List.Item.Meta
                                title={`${item.action} - ${item.success ? t('Success') : t('Failed')}`}
                                description={
                                    <>
                                        <div>{item.message}</div>
                                        {item.data?.session && (
                                            <Typography.Text type="secondary">
                                                Session: {item.data.session}
                                            </Typography.Text>
                                        )}
                                        {item.data?.file && (
                                            <Typography.Text type="secondary" style={{ display: 'block' }}>
                                                {t("File: {{file}}", { file: item.data.file })}
                                            </Typography.Text>
                                        )}
                                    </>
                                }
                            />
                        </List.Item>
                    )}
                />
            </Card>
        </Space>
    );
};

export default ShellTools;
